import { RefObject, useCallback, useRef } from 'react';
import styled from 'styled-components';

import { MenuItem } from '@szhsin/react-menu';
import {
  useDrfqDraftsFetchData,
  useDrfqDraftsIdsList,
  useDrfqDraftsService,
  useDrfqDraftsStore,
  useMaybeDrfqDraftsItem,
  useDrfqHistoryFetchData,
  useDrfqHistoryIdsList,
  useDrfqHistoryService,
  useDrfqService,
} from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import ActionsBarEmpty from '#/components/UnifiedRfqs/components/bottom-tabs/ActionsBarEmpty';
import {
  TrackingCtxProvider,
  useTrackAction,
} from '@paradigm/features/src/analytics';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';
import { useApi } from '@paradigm/api/src/fetch-api';
import toast from '#/components/toasts/toast';
import BaseTable from '../components/BaseTable';
import ContextMenuProvider, {
  useContextMenuActions,
  ContextMenu as BaseContextMenu,
  useContextMenuView,
} from '../context-menu/context-menu';
import RfqDetailsModal from '../drfq/rfq-details-bottom-table/RfqDetailsModal';
import RfqsHistoryRow, { RfqsHistoryHeaderRow } from './RfqsHistoryRow';

function ContextMenu() {
  const { openDetailsModal, createRfq } = useContextMenuActions();
  const { rfqId } = useContextMenuView();

  const datadogPrefix = 'RFQ History Table Context Menu';
  const trackAction = useTrackAction();
  const drfqService = useDrfqService();
  const drfqDraftsStore = useDrfqDraftsStore();
  const drfq = useMaybeDrfqDraftsItem(String(rfqId));

  // Only Draft RFQ can be closed
  const canCancelRfq = drfq?.state === 'DRAFT';

  const handleViewDetails = useCallback(() => {
    trackAction('view expired rfq');
    openDetailsModal();
  }, [openDetailsModal, trackAction]);

  const handleCreateRFQ = useCallback(() => {
    trackAction('create market', { auctionType: 'RFQ' });
    createRfq('RFQ');
  }, [createRfq, trackAction]);

  const handleCreateOB = useCallback(() => {
    trackAction('create market', { auctionType: 'OB' });
    createRfq('OB');
  }, [createRfq, trackAction]);

  const [, cancelDrfqApi] = useApi(drfqService.cancelDrfq);
  const cancelRfq = useCallback(() => {
    if (drfq == null) {
      return;
    }

    cancelDrfqApi({ rfqId: drfq.id }, (resp) => {
      if (!resp.ok) {
        drfqDraftsStore.setOne(drfq);
        toast(resp.message);
      }
    });
    drfqDraftsStore.removeOne(drfq.id);
  }, [cancelDrfqApi, drfq, drfqDraftsStore]);

  return (
    <BaseContextMenu>
      <CreateMarketMenuItem
        type="RFQ"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateRFQ}
      />
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateOB}
      />

      {canCancelRfq && (
        <MenuItem onClick={cancelRfq}>Cancel Draft RFQ</MenuItem>
      )}
      <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
    </BaseContextMenu>
  );
}

export default function RfqsHistoryTable({
  showDrafts,
}: {
  showDrafts: boolean;
}) {
  return (
    <TrackingCtxProvider
      value={showDrafts ? 'rfq drafts tab' : 'rfq history tab'}
    >
      <ContextMenuProvider>
        <RfqDetailsModal />
        <RfqsHistoryTableContent showDrafts={showDrafts} />
        <ContextMenu />
      </ContextMenuProvider>
    </TrackingCtxProvider>
  );
}
function RfqsHistoryTableContent({ showDrafts }: { showDrafts: boolean }) {
  const draftsFetchData = useDrfqDraftsFetchData();
  const historyFetchData = useDrfqHistoryFetchData();
  const draftsService = useDrfqDraftsService();
  const historyService = useDrfqHistoryService();
  const draftsIdsList = useDrfqDraftsIdsList();
  const historyIdsList = useDrfqHistoryIdsList();
  const scrollableRef = useRef<HTMLDivElement>(null);

  const { status, canFetchMore } = showDrafts
    ? draftsFetchData
    : historyFetchData;
  const { fetcher } = showDrafts ? draftsService : historyService;
  const drfqIds = showDrafts ? draftsIdsList : historyIdsList;

  return (
    <Container>
      <ActionsBarEmpty />
      <BaseTable
        status={status}
        canFetchMore={canFetchMore}
        fetcher={fetcher}
        isEmpty={drfqIds.length === 0}
        entityRowsList={
          <RfqHistoryRowsList
            drfqIds={drfqIds}
            scrollableRef={scrollableRef}
            showDrafts={showDrafts}
          />
        }
        headerRow={<RfqsHistoryHeaderRow />}
        dataTestId="unified-rfqs-history-table"
        scrollableRef={scrollableRef}
        emptyListMessage={
          showDrafts ? 'There are no Draft RFQs.' : 'There are no closed RFQs.'
        }
      />
    </Container>
  );
}

const Container = styled.div`
  flex: 1; // take 100% heigh
  display: flex;
  flex-direction: column;
`;

function RfqHistoryRowsList(props: {
  drfqIds: readonly Drfq['id'][];
  scrollableRef: RefObject<HTMLDivElement>;
  showDrafts: boolean;
}) {
  return (
    <>
      {props.drfqIds.map((id) => (
        <RfqsHistoryRow
          key={id}
          drfqId={id}
          scrollableRef={props.scrollableRef}
          showDrafts={props.showDrafts}
        />
      ))}
    </>
  );
}
